import type * as api from '@gitbook/api-client';
import type { NavigationScreenState } from '@gitbook/app-navigation';

import { getSpaceInSiteStructure } from '../../Components';

/**
 * We show the first 10 sites/spaces in the content sidebar by default
 * to reduce rendering time for orgs with lots of sites/spaces.
 */
export const SIDEBAR_VISIBLE_ITEMS_DEFAULT = 10;

/**
 * Whether the sidebar should render organization-level navigation.
 */
export function shouldShowOrgContentSidebar(
    screen: NavigationScreenState,
    orgAllContent: api.OrganizationAllContent
) {
    if (isOrgLevelScreen(screen)) {
        return true;
    }

    const siteId = 'site' in screen ? screen.site : undefined;
    if (!siteId) {
        return false;
    }

    // If the site ID in the URL doesn't match a site in the org
    // we show the org content sidebar and a `Not found` screen.
    const siteContent = orgAllContent.sites.find((item) => item.site.id === siteId);
    if (!siteContent) {
        return true;
    }

    // If we're in a space screen in a site workspace (here `siteId` is defined)
    if (screen.type === 'space') {
        const isSiteStyleguide = siteContent.site.styleguide?.space === screen.space;
        // We show the org sidebar if the space is not the site's style guide and is not in the site structure.
        return !isSiteStyleguide && !getSpaceInSiteStructure(siteContent.structure, screen.space);
    }
}

/**
 * Check whether a navigation screen belongs to the organization-level content sidebar.
 */
export function isOrgLevelScreen(screen: NavigationScreenState) {
    return (
        ['agents', 'home', 'sites', 'org_content', 'styleguides', 'trash'].includes(screen.type) ||
        (screen.type === 'space' && !screen.site)
    );
}
